import { useRef } from "react";
import {
  Bar, CartesianGrid, Cell, ComposedChart, Line, LabelList,
  ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { CORAL, DANGER, MUTED, NAVY } from "../constants";
import { formatCompactNumber, formatPercent, formatSeriesValue } from "../formatters";
import { useReferenceAreaBox } from "../hooks/useReferenceAreaBox";
import { HighlightCaption } from "./HighlightCaption";

export function TwoPanelChartPanel({ panelTitle, panel, series, height }) {
  const data = Array.isArray(panel?.data) ? panel.data : [];
  const barSeries = series.find((s) => s.type === "bar");
  const lineSeries = series.find((s) => s.type === "line");
  const currentIndexes = data.reduce((acc, row, i) => {
    if (row.is_current_quarter) acc.push(i);
    return acc;
  }, []);
  const hasHighlight = currentIndexes.length > 0;
  const highlightRange = hasHighlight
    ? [data[currentIndexes[0]].label, data[currentIndexes[currentIndexes.length - 1]].label]
    : null;
  const rightDomain = [panel?.axis_right_min ?? 0, panel?.axis_right_max ?? 100];
  const isSpendBar = Boolean(barSeries && /spend/i.test(barSeries.key));
  const leftAxisFormatter = (value) => (isSpendBar ? `$${formatCompactNumber(value)}` : formatCompactNumber(value));
  const chartWrapRef = useRef(null);
  const highlightBox = useReferenceAreaBox(hasHighlight, chartWrapRef, [data, height]);

  return (
    <div className="flex h-full min-w-0 flex-col">
      <p className="text-[11px] font-black uppercase tracking-[0.14em] mb-2" style={{ color: MUTED }}>
        {panelTitle}
      </p>
      {hasHighlight && (
        <div className="relative" style={{ height: 18 }}>
          <HighlightCaption box={highlightBox} />
        </div>
      )}
      <div ref={chartWrapRef} style={{ height }}>
        {!data.length ? (
          <div className="h-full flex items-center justify-center text-sm" style={{ color: MUTED }}>No data available.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 24, right: lineSeries ? 24 : 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f2" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                minTickGap={12}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: MUTED }} tickFormatter={leftAxisFormatter} axisLine={false} tickLine={false} width={40} />
              {lineSeries && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={rightDomain}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 10, fill: DANGER }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
              )}
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #d6dfeb" }}
                formatter={(value, name) => formatSeriesValue(name === lineSeries?.label ? lineSeries : barSeries, value)}
              />
              {highlightRange && (
                <ReferenceArea
                  yAxisId="left"
                  x1={highlightRange[0]}
                  x2={highlightRange[1]}
                  fill={NAVY}
                  fillOpacity={0.05}
                  stroke={NAVY}
                  strokeOpacity={0.15}
                />
              )}
              {barSeries && (
                <Bar yAxisId="left" dataKey={barSeries.key} name={barSeries.label} radius={[4, 4, 0, 0]}>
                  {data.map((row, i) => (
                    <Cell key={i} fill={row.is_current_quarter ? NAVY : "#aab8cf"} />
                  ))}
                  {barSeries.show_value_labels && (
                    <LabelList dataKey={barSeries.key} position="top" formatter={(value) => formatSeriesValue(barSeries, value)} style={{ fill: NAVY, fontSize: 10, fontWeight: 700 }} />
                  )}
                </Bar>
              )}
              {lineSeries && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey={lineSeries.key}
                  name={lineSeries.label}
                  stroke={CORAL}
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#fff", stroke: CORAL, strokeWidth: 2 }}
                  activeDot={{ r: 5 }}
                >
                  {lineSeries.show_value_labels && (
                    <LabelList dataKey={lineSeries.key} position="top" formatter={formatPercent} style={{ fill: CORAL, fontSize: 10, fontWeight: 700 }} />
                  )}
                </Line>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
